import type { ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button, Card } from "@axisnode/ui";

export function LoadingState({ label }: { label?: string }) {
  const { t } = useTranslation();
  return (
    <div role="status" data-testid="screen-loading" aria-busy="true">
      <Card>{label ?? t("screenStates.loading")}</Card>
    </div>
  );
}

export function EmptyState({
  title,
  description,
  action,
}: {
  title: string;
  description?: string;
  action?: ReactNode;
}) {
  return (
    <div data-testid="screen-empty">
      <Card title={title}>
        {description ? <p style={{ color: "var(--ax-color-text-muted)" }}>{description}</p> : null}
        {action}
      </Card>
    </div>
  );
}

export function ErrorState({
  title,
  message,
  onRetry,
}: {
  title?: string;
  message: string;
  onRetry?: () => void;
}) {
  const { t } = useTranslation();
  return (
    <div role="alert" data-testid="screen-error">
      <Card title={title ?? t("screenStates.error")}>
        <p>{message}</p>
        {onRetry ? (
          <Button variant="secondary" onClick={onRetry}>
            {t("screenStates.retry")}
          </Button>
        ) : null}
      </Card>
    </div>
  );
}

/** Business-state branches for advance_stage — distinct from ErrorState. */
export function AdvanceBlockedState({
  gapId,
  missing,
}: {
  gapId?: string;
  missing?: string[];
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="advance-blocked" role="status">
      <Card title={t("screenStates.advanceBlockedTitle")}>
        <p>
          {t("screenStates.advanceBlockedBody", {
            gapSuffix: gapId ? ` (${gapId})` : "",
          })}
        </p>
        {missing && missing.length > 0 ? (
          <ul>
            {missing.map((m) => (
              <li key={m}>{m}</li>
            ))}
          </ul>
        ) : null}
      </Card>
    </div>
  );
}

export function AdvanceWaitingState({
  reason,
  gapId,
  missionId,
}: {
  reason?: string;
  gapId?: string;
  missionId?: string;
}) {
  const { t } = useTranslation();
  return (
    <div data-testid="advance-waiting" role="status">
      <Card title={t("screenStates.advanceWaitingTitle")}>
        <p>
          {t("screenStates.advanceWaitingBody", {
            reasonSuffix: reason ? `: ${reason}` : "",
          })}
        </p>
        {gapId ? <p>{t("screenStates.gapLabel", { gapId })}</p> : null}
        {missionId ? <p>{t("screenStates.missionLabel", { missionId })}</p> : null}
      </Card>
    </div>
  );
}
